import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, List, Button, Typography, Alert, Spin, Tag, Layout, Avatar, Image, Divider } from 'antd';
import { DownloadOutlined, FileOutlined, CalendarOutlined, ToolOutlined } from '@ant-design/icons';
import { getDocumentsByToken } from '../../api/equipment';
import { getFileUrl } from '../../api/file';
import { formatDate } from '../../lib/utils';
import { getCompany } from '../../api/company';

const { Title, Text } = Typography;
const { Header } = Layout;

export default function EquipmentDocumentsView() {
    const { token } = useParams();
    const [loading, setLoading] = useState(true);
    const [equipmentData, setEquipmentData] = useState(null);
    const [error, setError] = useState(null);
    const [companyLogoUrl, setCompanyLogoUrl] = useState(null);

    useEffect(() => {
        const fetchDocuments = async () => {
            try {
                const result = await getDocumentsByToken(token);
                setEquipmentData(result);
            } catch (error) {
                console.error('Error fetching documents:', error);
                setError('Failed to load documents. The link may have expired or is invalid.');
            } finally {
                setLoading(false);
            }
        };

        if (token) {
            fetchDocuments();
        }

        // Fetch company logo
        (async function fetchCompanyLogo() {
            try {
                let company = await getCompany();
                setCompanyLogoUrl(getFileUrl(company.logo));
            } catch (error) {
                console.log("Error fetching company logo:", error);
                setCompanyLogoUrl(null);
            }
        })();
    }, [token]);

    const handleDownload = (doc) => {
        if (!doc.fileId) return;

        const file = {
            id: doc.fileId,
            name: doc.fileName,
            originalName: doc.fileOriginalName
        };

        const fileUrl = getFileUrl(file);
        const link = window.document.createElement('a');
        link.href = fileUrl;
        link.download = doc.fileOriginalName || doc.fileName || 'download';
        window.document.body.appendChild(link);
        link.click();
        window.document.body.removeChild(link);
    };

    const isExpired = (expireAt) => {
        if (!expireAt) return false;
        return new Date(expireAt) < new Date();
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-start bg-gray-100 min-h-screen">
                <Header className="flex items-center justify-between px-[16px] h-[48px] w-full bg-[#f19820]">
                    <h3 className="text-white font-bold text-lg leading-8 flex flex-col justify-center cursor-pointer">
                        TrakHire
                    </h3>
                </Header>
                <div className="flex items-center justify-center flex-grow">
                    <Spin size="large" tip="Loading documents..." />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-start bg-gray-100 min-h-screen">
                <Header className="flex items-center justify-between px-[16px] h-[48px] w-full bg-[#f19820]">
                    <h3 className="text-white font-bold text-lg leading-8 flex flex-col justify-center cursor-pointer">
                        TrakHire
                    </h3>
                </Header>
                <div className="w-full max-w-4xl mt-10 mb-10 px-4">
                    <Alert
                        message="Error"
                        description={error}
                        type="error"
                        showIcon
                        extra={[
                            <Link to={"/"} key="home">
                                <Button type="primary">
                                    Go Home
                                </Button>
                            </Link>
                        ]}
                    />
                </div>
            </div>
        );
    }

    if (!equipmentData || !equipmentData.documents || equipmentData.documents.length === 0) {
        return (
            <div className="flex flex-col items-center justify-start bg-gray-100 min-h-screen">
                <Header className="flex items-center justify-between px-[16px] h-[48px] w-full bg-[#f19820]">
                    <h3 className="text-white font-bold text-lg leading-8 flex flex-col justify-center cursor-pointer">
                        TrakHire
                    </h3>
                </Header>
                <div className="w-full max-w-4xl mt-10 mb-10 px-4">
                    <Alert
                        message="No Documents Found"
                        description="There are no documents available for this equipment."
                        type="info"
                        showIcon
                        extra={[
                            <Link to={"/"} key="home">
                                <Button type="primary">
                                    Go Home
                                </Button>
                            </Link>
                        ]}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col items-center justify-start bg-gray-100 min-h-screen">
            <Header className="flex items-center justify-between px-[16px] h-[48px] w-full bg-[#f19820]">
                <h3 className="text-white font-bold text-lg leading-8 flex flex-col justify-center cursor-pointer">
                    TrakHire
                </h3>
            </Header>

            <div className="w-full max-w-4xl mt-10 mb-10 px-4">
                <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                    {/* Header with logo */}
                    <div className="bg-gray-50 p-6 flex items-center justify-between border-b">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-800">
                                {equipmentData.equipmentName}
                            </h1>
                            <h3 className="text-xl">
                                Equipment Documents
                            </h3>
                        </div>
                        <div className="flex-shrink-0">
                            {companyLogoUrl ? (
                                <Image
                                    width={120}
                                    src={companyLogoUrl}
                                    alt="Company Logo"
                                    preview={false}
                                    className="object-contain"
                                />
                            ) : (
                                <Avatar shape="square" size={80} icon={<ToolOutlined />} />
                            )}
                        </div>
                    </div>

                    <div className="p-6">
                        {/* Documents List */}
                        <List
                            dataSource={equipmentData.documents}
                            renderItem={(doc) => (
                                <List.Item
                                    key={doc.id}
                                    className="bg-white hover:bg-gray-50 transition-colors px-4 py-3 border-b last:border-b-0"
                                    actions={[
                                        <Button
                                            type="primary"
                                            icon={<DownloadOutlined />}
                                            onClick={() => handleDownload(doc)}
                                            size="large"
                                        >
                                            Download
                                        </Button>
                                    ]}
                                >
                                    <List.Item.Meta
                                        avatar={
                                            <div className="bg-blue-100 rounded-full p-3">
                                                <FileOutlined style={{ fontSize: 24, color: '#1890ff' }} />
                                            </div>
                                        }
                                        title={
                                            <div className="flex items-center gap-2">
                                                <span className="text-base font-semibold">{doc.name}</span>
                                                {isExpired(doc.expireAt) && (
                                                    <Tag color="red">Expired</Tag>
                                                )}
                                            </div>
                                        }
                                        description={
                                            <div className="space-y-2 mt-2">
                                                <div className="flex items-center gap-2">
                                                    <FileOutlined className="text-gray-400" />
                                                    <Text type="secondary">
                                                        {doc.fileOriginalName || doc.fileName}
                                                    </Text>
                                                </div>
                                                {doc.expireAt && (
                                                    <div className="flex items-center gap-2">
                                                        <CalendarOutlined className="text-gray-400" />
                                                        <Text type={isExpired(doc.expireAt) ? 'danger' : 'secondary'}>
                                                            {isExpired(doc.expireAt) ? 'Expired on: ' : 'Expires on: '}
                                                            {formatDate(doc.expireAt)}
                                                        </Text>
                                                    </div>
                                                )}
                                            </div>
                                        }
                                    />
                                </List.Item>
                            )}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
